import { useEffect, useRef } from 'react';
import { FormInstance } from 'antd';

interface NationalAddressApiData {
  postalCode?: string;
  buildingNumber?: string;
  additionalNumber?: string;
  unitNumber?: string;
  street?: string;       // API field name — maps to form field 'streetName'
  streetName?: string;
  district?: string;     // API field name — maps to form field 'districtName'
  districtName?: string;
  fullAddress?: string;
  merchantName?: string;
  city?: string;
  regionName?: string;
  addressId?: string;
  status?: string;
}

interface SavedNationalAddress {
  postalCode?: string;
  buildingNumber?: string;
  additionalNumber?: string;
  unitNumber?: string;
  streetName?: string;
  districtName?: string;
  fullAddress?: string;
  merchantName?: string;
  city?: string;
  regionName?: string;
  addressId?: string;
  status?: string;
}

/**
 * Syncs national address API/payload data into the Ant Design form.
 * Uses a single `setFieldsValue` call (one atomic store update)
 * rather than per-field `setFields` calls.
 *
 * Priority: saved agent value > fresh API value — so existing agent edits are
 * never overwritten by a re-fetch.
 *
 * @param form      - The Ant Design form instance.
 * @param apiData   - Resolved national address (e.g. splAddress from payload).
 * @param savedData - Previously saved agent values from the payload, if any.
 */
const useNationalAddressFormSync = (
  form: FormInstance,
  apiData: NationalAddressApiData | undefined,
  savedData: SavedNationalAddress | undefined,
): void => {
  const lastSyncedKeyRef = useRef<string | undefined>();

  useEffect(() => {
    if (!apiData) return;

    const syncKey = `${apiData.addressId || ''}_${apiData.postalCode || ''}_${apiData.buildingNumber || ''}_${apiData.status || ''}`;
    if (lastSyncedKeyRef.current === syncKey) return;

    const saved = savedData ?? {};
    const merged: SavedNationalAddress = {
      postalCode: saved.postalCode ?? apiData.postalCode,
      buildingNumber: saved.buildingNumber ?? apiData.buildingNumber,
      additionalNumber: saved.additionalNumber ?? apiData.additionalNumber,
      unitNumber: saved.unitNumber ?? apiData.unitNumber,
      streetName: saved.streetName ?? apiData.streetName ?? apiData.street,
      districtName: saved.districtName ?? apiData.districtName ?? apiData.district,
      fullAddress: saved.fullAddress ?? apiData.fullAddress,
      merchantName: saved.merchantName ?? apiData.merchantName,
      city: saved.city ?? apiData.city,
      regionName: saved.regionName ?? apiData.regionName,
      addressId: saved.addressId ?? apiData.addressId,
      status: saved.status ?? apiData.status,
    };

    form.setFieldsValue({ reviewCompanyInformation: { nationalAddress: merged } });
    lastSyncedKeyRef.current = syncKey;
  }, [apiData, form, savedData]);
};

export default useNationalAddressFormSync;
